import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Server-verified admin gate.
 * Calls the `has_role(user_id, 'admin')` Postgres RPC before rendering children,
 * so the admin UI shell never paints for non-admin authenticated users.
 */
export const AdminRoute = ({ children }: AdminRouteProps) => {
  const [state, setState] = useState<'loading' | 'admin' | 'forbidden' | 'unauth'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (!cancelled) setState('unauth');
        return;
      }
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'admin' as const,
      });
      if (cancelled) return;
      if (error || !data) setState('forbidden');
      else setState('admin');
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (state === 'unauth') return <Navigate to="/login" replace />;
  if (state === 'forbidden') return <Navigate to="/" replace />;
  return <>{children}</>;
};
